import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { FiChevronsLeft, FiChevronLeft, FiChevronRight, FiChevronsRight } from 'react-icons/fi';
import { isLoggedIn } from '../../../utils/auth';
 import  CyberMemorialService  from '../../../services/FamilyPlaza/CyberMemorialService';
 import FamilyPlazaSubHeading from '../../../components/family-plaza/FamilyPlazaSubHeading';
 import FamilyPlazaTopSection from '../../../components/family-plaza/FamilyPlazaTopSection';

import { CgNametag } from 'react-icons/cg';

const CyberMemorialSearchPC = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchType, setSearchType] = useState('고인명');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isComposing, setIsComposing] = useState(false);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = searchResults.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(searchResults.length / itemsPerPage);

  // Generate page numbers array with sliding window
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 10;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }
    
    return pageNumbers;
  };

  // Click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNavigation = (route) => {
    // Navigate without showing any alerts for navigation links
    // Just navigate directly without any authentication checks
    navigate(route);
  };

  const handleSearchClick = async (result) => {
    // Store the selected person's data in localStorage
    const selectedPerson = {
      goinName: result.goinName,
      enshrineDay: result.enshrineDay,
      customerName: result.customerName,
      enshrineDate: result.enshrineDate,
      goinId: result.goinId,
      customerBirthday: result.customerBirthday,
      deathDate: result.deathDate,
    };
    localStorage.setItem('selectedPersonData', JSON.stringify(selectedPerson));
    
    // Navigate directly to donate page
    navigate('/donate');
  };

  // These functions are now handled in the profile section
  // Authentication and photo modals will be triggered from profile photo icon

  // API Functions


const searchGoins = async (name) => {
    try {
      setIsLoading(true);
      
      // Create search parameters based on search type
      let searchParams;
      if (searchType === '고인명') {
        searchParams = { deceasedName: name };
      } else if (searchType === '계약자명') {
        searchParams = { contractorName: name };
      } else {
        // Default to deceasedName if searchType is not recognized
        searchParams = { deceasedName: name };
      }
      
      const response = await CyberMemorialService.searchGoins(searchParams);

      if (response && response && Array.isArray(response)) {
        setSearchResults(response);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    const logged = localStorage.getItem('isLoggedIn') === 'true' || !!localStorage.getItem('authToken') || localStorage.getItem('modalAuthenticated') === 'true';
    if (!logged) {
      alert('회원전용 서비스입니다. 로그인을해주세요.');
      return;
    }
    
    if (searchQuery.trim()) {
      await searchGoins(searchQuery.trim());
    } else {
      // await loadAllGoins();
    }
    setShowSearchResults(true);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    // During IME composition (Korean input), do not filter
    if (isComposing || e.nativeEvent.isComposing) {
      setSearchQuery(value);
      return;
    }
    // Allow full Hangul ranges (Jamo + Compatibility Jamo + Syllables), English letters, spaces, and dots
    const textOnlyRegex = /^[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7A3a-zA-Z\s.]*$/;
    if (textOnlyRegex.test(value) || value === '') {
      setSearchQuery(value);
    }
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setShowDropdown(false);
  };

  // Pagination functions
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Load all goins API
const loadAllGoins = async () => {
    try {
      setIsLoading(true);
      const response = await CyberMemorialService.loadAllGoins();
      
      if (response && response && Array.isArray(response) && response.length > 0) {
        setSearchResults(response);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user just logged in and should show results
  useEffect(() => {
    const checkAndShowResults = async () => {
      if (isLoggedIn() && localStorage.getItem('showResultsAfterLogin') === 'true') {
        localStorage.removeItem('showResultsAfterLogin');
        await loadAllGoins();
        setShowSearchResults(true);
      }
    };
    
    checkAndShowResults();
  }, []);

  // Remove automatic data loading on component mount
  // Data will only be loaded when search icon is clicked

  return (
    <div className="announcement-list-page cyber-info-page-pc">

      <FamilyPlazaTopSection />

      <main className="main-content">

      <FamilyPlazaSubHeading heading="사이버추모관" />


      {/* Main Content */}
      <div className="cyber-memorial-search-main-content">
        <div className="cyber-memorial-search-page-content">
          <div className="cyber-memorial-search-container">
            
                         {/* Introduction */}
             <div className="cyber-memorial-search-introduction">
               <p>
                 사이버추모관은 추모원에 직접 방문하지 못해도 언제, 어디서나 가족이나 친지 등 고인에 대한 영정과 함께 추모의 글과 음악으로 고인을 추모할 수 있는 온라인 서비스입니다.
               </p>
                               <p className="cyber-memorial-search-introduction-bold">
                  추모원에 모셔진 고인을 아래의 검색창에 검색해주세요.
                </p>
             </div>

                         {/* Search Section */}
             <div className="cyber-memorial-search-section">
               <div className="cyber-memorial-search-form">
                 <div className="cyber-memorial-search-form-row">
                   <div className={`cyber-memorial-search-dropdown ${showDropdown ? 'open' : ''}`} ref={dropdownRef}>
                     <div 
                       className="cyber-memorial-search-dropdown-trigger"
                       onClick={() => setShowDropdown(!showDropdown)}
                     >
                       <span className="cyber-memorial-search-dropdown-selected">{searchType}</span>
                       <span className="cyber-memorial-search-dropdown-caret">^</span>
                     </div>
                     {showDropdown && (
                       <div className="cyber-memorial-search-dropdown-menu">
                         <div 
                           className={`cyber-memorial-search-dropdown-item ${searchType === '고인명' ? 'active' : ''}`}
                           onClick={() => handleSearchTypeChange('고인명')}
                         >
                           <span>고인명</span>
                         </div>
                         <div 
                           className={`cyber-memorial-search-dropdown-item ${searchType === '계약자명' ? 'active' : ''}`}
                           onClick={() => handleSearchTypeChange('계약자명')}
                         >
                           <span>계약자명</span>
                         </div>
                       </div>
                     )}
                   </div>
                   <div className="cyber-memorial-search-input">
                     <input 
                       type="text" 
                       placeholder="이름을 입력해 주세요" 
                       value={searchQuery}
                       onChange={handleSearchInputChange}
                       onCompositionStart={() => setIsComposing(true)}
                       onCompositionEnd={(e) => { setIsComposing(false); setSearchQuery(e.target.value); }}
                       onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                       lang="ko"
                       inputMode="text"
                       autoComplete="off"
                       spellCheck="false"
                       style={{ fontFamily: 'inherit' }}
                     />
                   </div>
                   <div className="cyber-memorial-search-button">
                     <button 
                       type="button" 
                       onClick={handleSearch}
                       disabled={isLoading}
                     >
                       {isLoading ? '검색중...' : '조회'}
                     </button>
                   </div>
                 </div>
               </div>
             </div>

             {/* Results Table */}
             {showSearchResults && (
               <div className="cyber-memorial-search-results">
                 {searchResults.length > 0 && (
                   <table className="cyber-memorial-search-table cyber-memorial-search-pc-table-unique">
                     <tbody>
                       {currentItems.map((result, index) => (
                         <tr key={index}>
                           <td>{result.goinName}</td>
                           <td>{result.enshrineDay} 안치</td>
                           <td>{result.customerName} 계약</td>
                           <td><button className="cyber-memorial-search-pc-record-search-btn" onClick={() => handleSearchClick(result)}><FaSearch /></button></td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                 )}
                 {searchQuery && searchResults.length === 0 && !isLoading && (
                   <div className="cyber-memorial-search-no-results">
                     검색 결과가 없습니다.
                   </div>
                 )}
               </div>
             )}

             {/* Pagination */}
             {showSearchResults && totalPages > 1 && (
               <div className="cyber-memorial-search-pagination">
                 <button 
                   className="cyber-memorial-search-pagination-btn" 
                   onClick={handleFirstPage}
                   disabled={currentPage === 1}
                 >
                   <FiChevronsLeft />
                 </button>
                 <button 
                   className="cyber-memorial-search-pagination-btn" 
                   onClick={handlePrevPage}
                   disabled={currentPage === 1}
                 >
                   <FiChevronLeft />
                 </button>
                 {getPageNumbers().map((page) => (
                   <button 
                     key={page}
                     className={`cyber-memorial-search-pagination-btn ${currentPage === page ? 'active' : ''}`}
                     onClick={() => handlePageChange(page)}
                   >
                     {page}
                   </button>
                 ))}
                 <button 
                   className="cyber-memorial-search-pagination-btn" 
                   onClick={handleNextPage}
                   disabled={currentPage === totalPages}
                 >
                   <FiChevronRight />
                 </button>
                 <button 
                   className="cyber-memorial-search-pagination-btn" 
                   onClick={handleLastPage}
                   disabled={currentPage === totalPages}
                 >
                   <FiChevronsRight />
                 </button>
               </div>
             )}

            {/* Information Box */}
            <div className="cyber-memorial-search-info-box">
              <div className="cyber-memorial-search-info-icon">!</div>
              <div className="cyber-memorial-search-info-content">
                <p>로그인 후 이용 가능합니다.</p>
                <p>• 조회가 안되는 회원분께서는 관리사무실 <span className="cyber-memorial-search-phone-number">1599-9442</span>로 연락바랍니다.</p>
              </div>
            </div>

          </div>
        </div>
      </div>

      </main>

             
     </div>
   );
 };

export default CyberMemorialSearchPC; 